import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import AdminPageHeader from "./AdminPageHeader";

export default function CaseTemplatesAdmin() {
  const { readOnly } = useOutletContext();
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [taxonomy, setTaxonomy] = useState({ taxonomy: {} });

  const load = async () => {
    try {
      const [t, tax] = await Promise.all([
        api.get("/admin/templates"),
        api.get("/config/issue-taxonomy"),
      ]);
      setRows(t.data);
      setTaxonomy(tax.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const issueTypes = useMemo(() => Object.keys(taxonomy.taxonomy || {}), [taxonomy]);

  const open = (r) =>
    setEditing(
      r || {
        name: "",
        issue_type: issueTypes[0] || "Conduct",
        specific_issue: "",
        case_details_template: "",
        follow_up_required_default: false,
        default_status: "New",
        suggested_outcome: "",
        active: true,
      }
    );

  const save = async () => {
    if (readOnly) return;
    try {
      if (editing.id) {
        await api.patch(`/admin/templates/${editing.id}`, editing);
        toast.success("Template updated");
      } else {
        await api.post("/admin/templates", editing);
        toast.success("Template created");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const toggle = async (r, v) => {
    if (readOnly) return;
    try {
      await api.patch(`/admin/templates/${r.id}`, { active: v });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div data-testid="admin-templates-page" className="space-y-5">
      <AdminPageHeader
        eyebrow="Admin · Templates"
        title="Case templates"
        description={
          <>
            Pre-fill the case form for common scenarios. Placeholders use{" "}
            <code className="rounded bg-[color:var(--surface-2)] px-1 py-0.5 text-xs">
              {"{{variable}}"}
            </code>
            .
          </>
        }
        rightSlot={
          !readOnly ? (
            <Button
              onClick={() => open(null)}
              data-testid="template-add-button"
              className="goodwin-primary-btn h-10 rounded-lg text-sm font-semibold text-white"
            >
              <Plus className="mr-1 h-4 w-4" strokeWidth={2} />
              New template
            </Button>
          ) : null
        }
      />

      <div className="brand-card overflow-hidden">
        <table className="w-full text-sm">
          <caption className="sr-only">Case templates</caption>
          <thead className="bg-[color:var(--surface-2)] text-left text-xs uppercase tracking-wider text-[color:var(--page-fg-muted)]">
            <tr>
              <th className="px-4 py-3" scope="col">Name</th>
              <th className="px-4 py-3" scope="col">Issue type</th>
              <th className="px-4 py-3" scope="col">Specific issue</th>
              <th className="px-4 py-3" scope="col">Active</th>
              <th className="px-4 py-3" scope="col"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[color:var(--border-subtle)]">
            {rows.map((r) => (
              <tr key={r.id} data-testid={`template-row-${r.id}`}>
                <td className="px-4 py-3 font-medium text-[color:var(--page-fg)]">{r.name}</td>
                <td className="px-4 py-3 text-[color:var(--page-fg-secondary)]">{r.issue_type}</td>
                <td className="px-4 py-3 text-[color:var(--page-fg-secondary)]">{r.specific_issue}</td>
                <td className="px-4 py-3">
                  <Switch
                    checked={!!r.active}
                    onCheckedChange={(v) => toggle(r, v)}
                    disabled={readOnly}
                    aria-label={`Toggle ${r.name} active`}
                  />
                </td>
                <td className="px-4 py-3 text-right">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => open(r)}
                    disabled={readOnly}
                    data-testid={`template-edit-${r.id}`}
                  >
                    Edit
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="brand-card p-5">
          <h2 className="text-base font-semibold text-[color:var(--page-fg)]">
            {editing.id ? "Edit template" : "Create template"}
          </h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <label className="text-xs text-[color:var(--page-fg-muted)]">Name</label>
              <Input
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                aria-label="Template name"
                data-testid="template-edit-name"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-[color:var(--page-fg-muted)]">Default status</label>
              <Select value={editing.default_status} onValueChange={(v) => setEditing({ ...editing, default_status: v })}>
                <SelectTrigger data-testid="template-edit-default-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["New", "In Progress", "Under Investigation", "Pending Follow-up"].map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-[color:var(--page-fg-muted)]">Issue type</label>
              <Select
                value={editing.issue_type}
                onValueChange={(v) => setEditing({ ...editing, issue_type: v, specific_issue: "" })}
              >
                <SelectTrigger data-testid="template-edit-issue-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {issueTypes.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-[color:var(--page-fg-muted)]">Specific issue</label>
              <Select value={editing.specific_issue} onValueChange={(v) => setEditing({ ...editing, specific_issue: v })}>
                <SelectTrigger data-testid="template-edit-specific-issue">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {(taxonomy.taxonomy?.[editing.issue_type] || []).map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1 sm:col-span-2">
              <label className="text-xs text-[color:var(--page-fg-muted)]">Case details template</label>
              <Textarea
                rows={6}
                value={editing.case_details_template || ""}
                onChange={(e) => setEditing({ ...editing, case_details_template: e.target.value })}
                aria-label="Case details template"
                data-testid="template-edit-details"
              />
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={!!editing.follow_up_required_default}
                onCheckedChange={(v) => setEditing({ ...editing, follow_up_required_default: v })}
                aria-label="Follow-up required default"
                data-testid="template-edit-followup"
              />
              <label className="text-sm text-[color:var(--page-fg)]">Follow-up required by default</label>
            </div>
          </div>
          <div className="mt-5 flex gap-2">
            <Button onClick={save} data-testid="template-save" className="goodwin-primary-btn text-white">
              Save
            </Button>
            <Button variant="outline" onClick={() => setEditing(null)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
